import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/db/db';
import type { BackupLogEntry, DriveState, Uuid } from '@/domain/types';
import { deriveStorageStatus, type StorageStatus } from './storageStatus';
import { useSettings } from './useSettings';
import { useOnline } from './useOnline';

export interface StorageStatusResult {
  status: StorageStatus;
  /** "Google Drive" / "Koofr" — for the strings that must name the backend. */
  providerLabel: string;
  state: DriveState | null | undefined;
}

/** Storage pill state for a company: the `driveState` row, network state and active provider. */
export function useStorageStatus(companyId: Uuid | null | undefined): StorageStatusResult {
  const online = useOnline();
  const provider = useSettings()?.cloud.provider;
  const providerLabel = provider === 'koofr' ? 'Koofr' : 'Google Drive';
  const state = useLiveQuery(async () => {
    if (!companyId) return null;
    return (await db.driveState.get(companyId)) ?? null;
  }, [companyId]);
  return { status: deriveStorageStatus(companyId ? state : null, online, undefined, providerLabel), state, providerLabel };
}

/** Latest backup log rows, newest first. */
export function useBackupLog(companyId: Uuid | null | undefined, limit = 10): BackupLogEntry[] {
  return (
    useLiveQuery(async () => {
      if (!companyId) return [];
      return db.backupLog.where('companyId').equals(companyId).reverse().sortBy('at').then((rows) => rows.slice(0, limit));
    }, [companyId, limit]) ?? []
  );
}
